import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ErrorScreen from "core/components/ErrorScreen";
import NoMatchScreen from "arena/components/NoMatchScreen";
import { useSearchParams } from "react-router-dom";
import useIsMobile from "arena/hooks/useIsMobile";
import ShortyVirtualItem from "./ShortyVirtualItem";
import ShortyPlaceholder from "./ShortyPlaceholder";
import ShortyLoadingSkeleton from "./ShortyLoadingSkeleton";
import ShortyCommentsDrawer from "./ShortyCommentsDrawer";
import ShortyVideoOverlay from "./ShortyVideoOverlay";
import useMainVideoPlayer from "./hooks/useMainVideoPlayer";
import useVideoPositioning from "./hooks/useVideoPositioning";
import useContentLoader from "./hooks/useContentLoader";
import useVideoNavigation from "./hooks/useVideoNavigation";

export default function ShortyVirtualFeed({
  initialContentId,
  onHelmetContentChange,
}) {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const collectionId = searchParams.get("collectionId");
  const isMobile = useIsMobile();

  // Состояние для комментариев
  const [commentsOpen, setCommentsOpen] = useState(false);

  // Функции для управления комментариями
  const handleOpenComments = () => {
    setCommentsOpen(true);
  };

  const handleCloseComments = () => {
    setCommentsOpen(false);
  };

  // Загрузка контента
  const { contents, loadingMore, initialLoading, error, handleLoadMore } =
    useContentLoader({
      initialContentId,
      productId,
      collectionId,
    });

  // Навигация между видео
  const { activeIndex, handleActivateDebounced, handleNavigate, cleanup } =
    useVideoNavigation({
      contents,
      loadingMore,
      handleLoadMore,
    });

  // Главный видео плеер
  const {
    mainVideoRef,
    mainVideoContainerRef,
    isMainVideoMounted,
    activeVideoData,
    videoState,
    videoActions,
    handleActiveVideoReady,
  } = useMainVideoPlayer({
    initialLoading,
  });

  // Позиционирование главного видео
  useVideoPositioning({
    mainVideoContainerRef,
    activeIndex,
    contentsLength: contents.length,
    isMobile,
  });

  useEffect(() => {
    if (onHelmetContentChange && contents[activeIndex]) {
      onHelmetContentChange(contents[activeIndex]);
    }
  }, [activeIndex, contents, onHelmetContentChange]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      // Останавливаем видео при закрытии диалога (критично для iOS)
      if (mainVideoRef.current && !mainVideoRef.current.paused) {
        mainVideoRef.current.pause();
      }
      cleanup();
    };
  }, [cleanup, mainVideoRef]);

  const RENDER_WINDOW_SIZE = 3;

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (initialLoading) {
    return <ShortyLoadingSkeleton count={2} />;
  }

  if (contents.length === 0 && !initialLoading) {
    return <NoMatchScreen />;
  }

  // Вычисляем диапазон элементов для рендеринга
  const renderStartIndex = Math.max(0, activeIndex - RENDER_WINDOW_SIZE);
  const renderEndIndex = Math.min(
    contents.length - 1,
    activeIndex + RENDER_WINDOW_SIZE
  );

  return (
    <Box
      data-shorty-feed
      sx={{
        height: isMobile
          ? "100dvh" // Dynamic viewport height для мобильных - исключает панельки браузера
          : "calc(100vh - 64px)", // На десктопе обычный vh минус header
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden", // Предотвращаем горизонтальный скролл
        scrollSnapType: "y mandatory",
        scrollBehavior: "smooth",
        position: "relative",
      }}
    >
      {/* Контейнер для главного video элемента с контролами */}
      <div
        ref={mainVideoContainerRef}
        id="main-video-container"
        style={{
          position: "absolute",
          zIndex: 100,
          display: "none", // Скрыт по умолчанию
          overflow: "visible", // Разрешаем элементам управления выходить за границы видео
        }}
      >
        <ShortyVideoOverlay
          mainVideoRef={mainVideoRef}
          activeVideoData={activeVideoData}
          videoState={videoState}
          videoActions={videoActions}
          activeIndex={activeIndex}
          contentsLength={contents.length}
          onNavigate={handleNavigate}
          onOpenComments={handleOpenComments}
        />
      </div>
      {contents.map((content, index) => {
        // Проверяем, находится ли элемент в окне рендеринга
        const isInRenderWindow =
          index >= renderStartIndex && index <= renderEndIndex;

        // Для элементов вне окна рендеринга показываем легковесный placeholder
        if (!isInRenderWindow) {
          return <ShortyPlaceholder key={`${content.id}-${index}`} />;
        }

        // Определяем нужно ли предзагружать элемент (2 вперед и 2 назад)
        const shouldPreload =
          (index === activeIndex + 1 && index < contents.length) || // следующий
          (index === activeIndex + 2 && index < contents.length) || // через один вперед
          (index === activeIndex - 1 && index >= 0) || // предыдущий
          (index === activeIndex - 2 && index >= 0); // через один назад

        // Для элементов в окне рендеринга показываем полный компонент
        return (
          <ShortyVirtualItem
            key={`${content.id}-${index}`}
            content={content}
            productId={productId}
            collectionId={collectionId}
            index={index}
            isActive={index === activeIndex}
            shouldPreload={shouldPreload}
            onActivate={() => handleActivateDebounced(index)}
            onNavigate={handleNavigate}
            totalItems={contents.length}
            mainVideoRef={index === activeIndex ? mainVideoRef : null}
            isMainVideoMounted={
              index === activeIndex ? isMainVideoMounted : false
            }
            onActiveVideoReady={
              index === activeIndex ? handleActiveVideoReady : null
            }
          />
        );
      })}

      {/* Drawer для комментариев */}
      {activeVideoData && (
        <ShortyCommentsDrawer
          content={activeVideoData.content}
          open={commentsOpen}
          onClose={handleCloseComments}
          onOpen={handleOpenComments}
        />
      )}
    </Box>
  );
}

ShortyVirtualFeed.propTypes = {
  initialContentId: PropTypes.string,
  onHelmetContentChange: PropTypes.func,
};
